import { FC, memo, useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiActions,
  MiContentDrawer,
  MiDrawerHeader,
  MiLoader,
  MiMenu,
  MiModalConfirm,
  MiTab,
} from "@miplatform/components";
import {
  APP_BAR_HEIGHT,
  APP_BAR_HEIGHT_TABLET,
  ATTEMPT_STATUSES,
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  HTTP_STATUSES,
  MIBUILD_PAGE_HEADER_HEIGHT,
} from "@miplatform/constants";
import { useBoolean } from "@miplatform/hooks";
import {
  Colors,
  IApiResponse,
  IAttempt,
  LoaderType,
  StepStatus,
  WorkOrderStatus,
} from "@miplatform/types";
import { mdiClose, mdiHomeOutline, mdiPencilOutline, mdiReload } from "@mdi/js";
import { useWorkOrder } from "@mibuildweb/hooks";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { WorkOrderDrawerTabs } from "./components/WorkOrderAttemptDrawer.types";
import { WorkOrderDrawerSubheader } from "./components/WorkOrderDrawerSubHeader";
import { WorkOrderEdit } from "./components/WorkOrderEdit/WorkOrderEdit";
import { WorkOrderDrawerContactsTab } from "./components/tabs/WorkOrderDrawerContactsTab";
import { WorkOrderDrawerDetailsTab } from "./components/tabs/WorkOrderDrawerDetailsTab";
import { WorkOrderDrawerDocumentsTab } from "./components/tabs/WorkOrderDrawerDocumentsTab";
import { WorkOrderDrawerHistoryTab } from "./components/tabs/WorkOrderDrawerHistoryTab";
import { WorkOrderDrawerStepsTab } from "./components/tabs/WorkOrderDrawerStepsTab";
import { MiAttemptNumberBadge } from "../../../components/MiAttemptNumberBadge/MiAttemptNumberBadge";
import { CalendarSetsConfigDataItem } from "../../../stores/calendarsStore/calendarsStore.types";
import { HoldAttemptFormData } from "../NhcCalendar.types";
import { WorkOrderAttemptDrawerActivity } from "../WorkOrderAttemptActivity/WorkOrderAttemptDrawerActivity";
import { WorkOrderHoldModal } from "../WorkOrderHoldModal/WorkOrderHoldModal";
import { useWorkOrderAttemptDrawerViewModel } from "./useWorkOrderAttemptDrawerViewModel";

export const WorkOrderAttemptDrawer: FC<WorkOrderAttemptDrawerProps> = memo(
  ({
    open = false,
    isReadOnly = false,
    listAttempt,
    isAssigneeMode,
    isCalendarLoading,
    calendarSet,
    startWorkOrder,
    updateAttempt,
    holdWorkOrder,
    resetWorkOrder,
    highlightAttempts,
    submitWorkOrder,
    closeWorkOrder,
    getCalendarCounts,
    onClose,
  }) => {
    const theme = useTheme();
    const { classes } = useStyles();
    const isTablet = useMediaQuery(theme.breakpoints.down("md"));
    const { address } = listAttempt?.home ?? {};

    const [activeTab, setActiveTab] = useState<WorkOrderDrawerTabs>(
      WorkOrderDrawerTabs.Steps
    );

    const {
      attempt: fetchedAttempt,
      isLoading,
      stateManager,
      setAttempt,
      getAttempt,
      updateAttemptDates,
    } = useWorkOrderAttemptDrawerViewModel({
      listAttempt,
      updateAttempt,
    });
    const [isHoldModalOpen, handleHoldModalOpen, handleHoldModalClose] =
      useBoolean();
    const [isEditMode, turnOnEditMode, turnOffEditMode] = useBoolean();
    const [isResetModalOpen, handleResetModalOpen, handleResetModalClose] =
      useBoolean();

    const {
      workOrder,
      getWorkOrder,
      isLoading: isWorkOrderLoading,
    } = useWorkOrder(listAttempt.assignment?.workOrder?.workOrderId ?? null);

    const tabs = [
      {
        label: "Steps",
        value: WorkOrderDrawerTabs.Steps,
      },
      {
        label: "Documents",
        value: WorkOrderDrawerTabs.Documents,
      },
      {
        label: "Details",
        value: WorkOrderDrawerTabs.Details,
      },
      {
        label: "Contacts",
        value: WorkOrderDrawerTabs.Contacts,
      },
      {
        label: "History",
        value: WorkOrderDrawerTabs.History,
      },
      {
        label: "Activity",
        value: WorkOrderDrawerTabs.Activity,
      },
    ];

    const DRAWER_TOP_OFFSET =
      (isTablet ? APP_BAR_HEIGHT_TABLET : APP_BAR_HEIGHT) +
      MIBUILD_PAGE_HEADER_HEIGHT;

    const attempt = useMemo(
      () => ({ ...listAttempt, ...fetchedAttempt }),
      [fetchedAttempt, listAttempt]
    );
    const isResetDisabled = useMemo(
      () => workOrder?.status !== WorkOrderStatus.Complete,
      [workOrder?.status]
    );
    const isEditDisabled = useMemo(
      () =>
        !attempt.steps?.some(({ stepResult }) => stepResult) &&
        attempt.steps?.length,
      [attempt?.steps]
    );
    const isCompleteDisabled = useMemo(
      () =>
        attempt.steps
          ?.filter(({ isRequired }) => isRequired)
          .find(
            ({ stepResult, status }) =>
              stepResult?.status !== StepStatus.Completed &&
              status !== StepStatus.Completed
          ) ||
        isCalendarLoading ||
        isLoading ||
        workOrder?.status !== WorkOrderStatus.InProgress,
      [attempt?.steps, isCalendarLoading, isLoading, workOrder?.status]
    );
    const canHold = useMemo(
      () =>
        !(
          attempt.attemptStatus === ATTEMPT_STATUSES.HOLD ||
          attempt.attemptStatus === ATTEMPT_STATUSES.CANCELLED ||
          attempt.attemptStatus === ATTEMPT_STATUSES.CLOSED
        ),
      [attempt]
    );

    const handleClose = () => onClose(listAttempt.id);

    const handleRefresh = () => {
      getWorkOrder();
      getAttempt();
    };

    const handleReset = () => {
      resetWorkOrder?.(listAttempt, handleRefresh);
      handleResetModalClose();
    };

    const submitHold = async (data: HoldAttemptFormData) => {
      if (!holdWorkOrder) {
        return;
      }

      const response = await holdWorkOrder(data);

      if (response.status === HTTP_STATUSES.OK) {
        handleHoldModalClose();
        getCalendarCounts?.();
        updateAttempt?.(response.data as IAttempt);
        handleClose();
      }
    };

    const handleReassignClick = () => {
      highlightAttempts?.(listAttempt.id);
      handleClose();
    };

    const handleStartClick = async () => {
      if (!startWorkOrder) {
        return;
      }

      const response = await startWorkOrder(attempt);

      if (response) {
        getWorkOrder();
        updateAttempt?.(response);
        setAttempt(response);
      }
    };

    const handleCompleteClick = async () => {
      if (!submitWorkOrder || !closeWorkOrder) {
        return;
      }

      const submittedAttempt = await submitWorkOrder(attempt);

      if (!submittedAttempt) {
        return;
      }

      const response = await closeWorkOrder(submittedAttempt);

      if (response.status === HTTP_STATUSES.OK) {
        updateAttempt?.(response.data);
        setAttempt(response.data);
        handleClose();
      }
    };

    useEffect(() => {
      if (open) {
        getWorkOrder();
        getAttempt();
      }
    }, [open, listAttempt.id]);

    return (
      <MiContentDrawer
        variant="persistent"
        isLoading={isLoading || isWorkOrderLoading}
        open={open}
        onClose={handleClose}
        hideBackdrop
        sx={{
          "& > .MuiPaper-root": {
            top: DRAWER_TOP_OFFSET,
            height: `calc(100% - ${DRAWER_TOP_OFFSET}px)`,
          },
        }}
        actions={
          isReadOnly
            ? []
            : [
                {
                  variant: BUTTON_VARIANTS.TEXT,
                  color: BUTTON_COLORS.BLACK,
                  name: `Cancel`,
                  onClick: isEditMode ? turnOffEditMode : handleClose,
                },
                canHold &&
                  !isEditMode && {
                    variant: BUTTON_VARIANTS.OUTLINED,
                    name: "Hold Work Order",
                    color: BUTTON_COLORS.PURPLE,
                    onClick: handleHoldModalOpen,
                  },
                !isEditMode && {
                  variant: BUTTON_VARIANTS.CONTAINED,
                  disabled: isCompleteDisabled,
                  title: `Complete ${attempt.assignment?.assignmentTemplateName}`,
                  onClick: handleCompleteClick,
                },
              ].filter(Boolean)
        }
      >
        <Box className={classes.content}>
          <MiDrawerHeader
            title={listAttempt?.home?.address.addressLine1 || ""}
            subtitle={[
              address ? `${address.city}, ${address.regionCode}` : "",
              listAttempt?.home?.communityName ?? "",
              listAttempt?.home?.builderName ?? "",
            ]}
            leftIcon={mdiHomeOutline}
            rightAction={
              <MiActions
                type="ICONS"
                actions={[
                  {
                    name: "Close",
                    icon: mdiClose,
                    onClick: handleClose,
                  },
                ]}
              />
            }
          />
          <MiLoader isLoading={isLoading} type={LoaderType.Linear} />
          <Box className={classes.titleContainer}>
            <Typography className={classes.title}>
              {isEditMode && (
                <Typography
                  component="span"
                  fontWeight={theme.fontWeight.semiBold}
                  color={theme.palette.primary.medium}
                >
                  Editing:{" "}
                </Typography>
              )}
              {attempt.assignment?.workOrder?.workOrderTemplateName}
              <MiAttemptNumberBadge
                attemptNumber={attempt.attemptNumber}
                className={classes.attemptNumberBadge}
              />
            </Typography>
            {!isEditMode && !isReadOnly && (
              <MiMenu
                data={[
                  {
                    label: "Edit Work Order",
                    icon: mdiPencilOutline,
                    color: theme.palette.grey.medium,
                    disabled: isEditDisabled,
                    action: turnOnEditMode,
                  },
                  {
                    label: "Reset Work Order",
                    icon: mdiReload,
                    color: theme.palette.error.main,
                    disabled: isResetDisabled,
                    action: handleResetModalOpen,
                  },
                ]}
                data-testid="work-order-drawer-menu"
              />
            )}
          </Box>
          {isEditMode ? (
            <WorkOrderEdit
              attempt={attempt}
              workOrder={workOrder}
              isLoading={isLoading || isWorkOrderLoading}
              getAttempt={getAttempt}
              onUpdateAttemptDates={updateAttemptDates}
            />
          ) : (
            <>
              <Box className={classes.subheaderContainer}>
                <WorkOrderDrawerSubheader attempt={attempt} />
              </Box>
              <Box className={classes.tabContainer}>
                <MiTab
                  isSecondary
                  tabs={tabs}
                  onTabChange={setActiveTab}
                  currentTab={activeTab}
                />
              </Box>
              <Box className={classes.tabContent}>
                {activeTab === WorkOrderDrawerTabs.Steps && (
                  <WorkOrderDrawerStepsTab
                    isAssigneeMode={isAssigneeMode}
                    isReadOnly={isReadOnly}
                    attempt={attempt}
                    workOrder={workOrder}
                    isLoading={isLoading || isCalendarLoading}
                    getAttempt={getAttempt}
                    onStartClick={handleStartClick}
                    onReassignClick={handleReassignClick}
                  />
                )}
                {activeTab === WorkOrderDrawerTabs.Documents && workOrder && (
                  <WorkOrderDrawerDocumentsTab attempt={attempt} />
                )}
                {activeTab === WorkOrderDrawerTabs.History && (
                  <WorkOrderDrawerHistoryTab />
                )}
                {activeTab === WorkOrderDrawerTabs.Activity && (
                  <WorkOrderAttemptDrawerActivity
                    attempt={attempt}
                    stateManager={stateManager}
                  />
                )}
                {activeTab === WorkOrderDrawerTabs.Details && (
                  <WorkOrderDrawerDetailsTab
                    home={attempt.home || null}
                    attempt={attempt}
                  />
                )}
                {activeTab === WorkOrderDrawerTabs.Contacts && (
                  <WorkOrderDrawerContactsTab homeId={listAttempt.home?.id} />
                )}
              </Box>
            </>
          )}
        </Box>
        {!isReadOnly && (
          <>
            <WorkOrderHoldModal
              open={isHoldModalOpen}
              onClose={handleHoldModalClose}
              onHold={(data) => submitHold({ ...data, attempt })}
              calendarSet={calendarSet?.config ?? null}
              isLoading={!!isCalendarLoading || isLoading}
            />
            <MiModalConfirm
              title="Reset Work Order"
              titleIcon={{
                path: mdiReload,
                color: theme.palette.error.main,
              }}
              disableClick={isLoading}
              textPositive="Reset Work Order"
              open={isResetModalOpen}
              handleNegative={handleResetModalClose}
              handlePositive={handleReset}
              description="Resetting this work order will reset its status as well as remove any progress that has been made on this work order."
              buttonPositiveColor={Colors.Error}
            />
          </>
        )}
      </MiContentDrawer>
    );
  }
);

interface WorkOrderAttemptDrawerProps {
  listAttempt: IAttempt;
  open?: boolean;
  isReadOnly?: boolean;
  isAssigneeMode?: boolean;
  isCalendarLoading?: boolean;
  calendarSet?: CalendarSetsConfigDataItem | null;
  startWorkOrder?: (attempt: IAttempt) => Promise<IAttempt | null>;
  updateAttempt?: (attempt: IAttempt) => void;
  holdWorkOrder?: (
    data: HoldAttemptFormData
  ) => Promise<IApiResponse<IAttempt | null>>;
  resetWorkOrder?: (attempt: IAttempt, onRefresh?: () => void) => void;
  highlightAttempts?: (attemptId: string) => void;
  submitWorkOrder?: (attempt: IAttempt) => Promise<IAttempt | null>;
  closeWorkOrder?: (attempt: IAttempt) => Promise<IApiResponse<IAttempt>>;
  getCalendarCounts?: () => void;
  onClose: (attemptId: string) => void;
}

const useStyles = makeStyles()((theme) => ({
  drawer: {
    borderLeft: `${theme.spacing(2)} solid ${theme.palette.grey.accent}`,
    boxSizing: "content-box",
    overflow: "auto",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    width: "100%",
  },
  titleContainer: {
    display: "flex",
    alignItems: "center",
    paddingRight: theme.spacing(4),
  },
  tabContainer: {
    borderBottom: `1px solid ${theme.palette.grey.medium}`,
    zIndex: theme.zIndex.appBar,
    position: "sticky",
    top: 0,
  },
  title: {
    fontWeight: theme.fontWeight.semiBold,
    padding: theme.spacing(0, 4, 0, 4),
    flex: 1,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  attemptNumberBadge: {
    marginLeft: theme.spacing(2),
  },
  subheaderContainer: {
    padding: theme.spacing(4),
  },
  tabContent: {
    padding: theme.spacing(4),
    height: "100%",
    overflow: "auto",
  },
}));
